import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './FeedbackListPage.css';

interface Attachment {
  id: number;
  fileName: string;
  filePath: string;
  contentType?: string;
  fileSize: number;
  uploadedDate: string;
  description?: string;
}

interface Feedback {
  id: number;
  patientName?: string;
  patientPhone?: string;
  email?: string;
  categoryName: string;
  location?: string;
  subject: string;
  description: string;
  priority: string;
  status: string;
  createdDate: string;
  resolvedDate?: string;
  adminResponse?: string;
  rating: number;
  qrCodeSource?: string;
  attachments: Attachment[];
}

const FeedbackListPage: React.FC = () => {
  const { token } = useAuth();
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    loadFeedbacks();
  }, [token]);

  const loadFeedbacks = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get('/feedback', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setFeedbacks(response.data);
    } catch (err: any) {
      console.error('Feedbacks yüklenirken hata:', err);
      setError(err.response?.data?.message || 'Geri bildirimler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'high': return '#ff5722';
      case 'medium': return '#ff9800';
      case 'low': return '#4caf50';
      default: return '#757575';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'open': return '#2196f3';
      case 'inprogress': return '#ff9800';
      case 'resolved': return '#4caf50';
      case 'closed': return '#757575';
      default: return '#757575';
    }
  };

  const handleViewDetails = (feedback: Feedback) => {
    setSelectedFeedback(feedback);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedFeedback(null);
  };

  const downloadAttachment = (attachment: Attachment) => {
    const downloadUrl = `http://172.173.131.89:86/${attachment.filePath}`;
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = attachment.fileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const isImage = (contentType: string) => {
    return contentType?.startsWith('image/');
  };

  if (loading) {
    return (
      <div className="feedback-list-container">
        <div className="loading-message">Geri bildirimler yükleniyor...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="feedback-list-container">
        <div className="error-message">
          <p>Hata: {error}</p>
          <button onClick={loadFeedbacks} className="retry-button">
            Tekrar Dene
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="feedback-list-container">
      <div className="feedback-list-paper">
        <div className="feedback-list-header">
          <h1 className="feedback-list-title">
            Geri Bildirimleri ({feedbacks.length})
          </h1>
          <button onClick={loadFeedbacks} className="refresh-button">
            Yenile
          </button>
        </div>

        {feedbacks.length === 0 ? (
          <div className="no-feedback-message">
            <p>Henüz geri bildirim bulunmamaktadır.</p>
          </div>
        ) : (
          <div className="feedback-table-container">
            <table className="feedback-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Hasta Adı</th>
                  <th>Kategori</th>
                  <th>Konu</th>
                  <th>Öncelik</th>
                  <th>Durum</th>
                  <th>Puan</th>
                  <th>Tarih</th>
                  <th>Ekler</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {feedbacks.map((feedback) => (
                  <tr key={feedback.id}>
                    <td>{feedback.id}</td>
                    <td>{feedback.patientName || 'Anonim'}</td>
                    <td>
                      <span className="category-badge">
                        {feedback.categoryName}
                      </span>
                    </td>
                    <td className="subject-cell" title={feedback.description}>
                      {feedback.subject}
                    </td>
                    <td>
                      <span
                        className="priority-badge"
                        style={{ backgroundColor: getPriorityColor(feedback.priority) }}
                      >
                        {feedback.priority}
                      </span>
                    </td>
                    <td>
                      <span
                        className="status-badge"
                        style={{ backgroundColor: getStatusColor(feedback.status) }}
                      >
                        {feedback.status}
                      </span>
                    </td>
                    <td>
                      <div className="rating-display">
                        {'★'.repeat(feedback.rating)}
                        {'☆'.repeat(5 - feedback.rating)}
                      </div>
                    </td>
                    <td className="date-cell">
                      {new Date(feedback.createdDate).toLocaleDateString('tr-TR')}
                    </td>
                    <td>
                      {feedback.attachments.length > 0 && (
                        <span className="attachment-count">
                          📎 {feedback.attachments.length}
                        </span>
                      )}
                    </td>
                    <td>
                      <button
                        onClick={() => handleViewDetails(feedback)}
                        className="details-button"
                      >
                        Detaylar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Feedback Detail Modal */}
      {showModal && selectedFeedback && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Geri Bildirim Detayları</h2>
              <button onClick={closeModal} className="close-button">×</button>
            </div>

            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-item">
                  <label>ID:</label>
                  <span>{selectedFeedback.id}</span>
                </div>

                <div className="detail-item">
                  <label>Hasta Adı:</label>
                  <span>{selectedFeedback.patientName || 'Anonim'}</span>
                </div>

                <div className="detail-item">
                  <label>Telefon:</label>
                  <span>{selectedFeedback.patientPhone || 'Belirtilmemiş'}</span>
                </div>

                <div className="detail-item">
                  <label>E-posta:</label>
                  <span>{selectedFeedback.email || 'Belirtilmemiş'}</span>
                </div>

                <div className="detail-item">
                  <label>Kategori:</label>
                  <span className="category-badge">{selectedFeedback.categoryName}</span>
                </div>

                <div className="detail-item">
                  <label>Lokasyon:</label>
                  <span>{selectedFeedback.location || 'Belirtilmemiş'}</span>
                </div>

                <div className="detail-item">
                  <label>Öncelik:</label>
                  <span
                    className="priority-badge"
                    style={{ backgroundColor: getPriorityColor(selectedFeedback.priority) }}
                  >
                    {selectedFeedback.priority}
                  </span>
                </div>

                <div className="detail-item">
                  <label>Durum:</label>
                  <span
                    className="status-badge"
                    style={{ backgroundColor: getStatusColor(selectedFeedback.status) }}
                  >
                    {selectedFeedback.status}
                  </span>
                </div>

                <div className="detail-item">
                  <label>Puan:</label>
                  <div className="rating-display">
                    {'★'.repeat(selectedFeedback.rating)}
                    {'☆'.repeat(5 - selectedFeedback.rating)}
                  </div>
                </div>

                <div className="detail-item">
                  <label>Oluşturma Tarihi:</label>
                  <span>{new Date(selectedFeedback.createdDate).toLocaleString('tr-TR')}</span>
                </div>

                {selectedFeedback.resolvedDate && (
                  <div className="detail-item">
                    <label>Çözüm Tarihi:</label>
                    <span>{new Date(selectedFeedback.resolvedDate).toLocaleString('tr-TR')}</span>
                  </div>
                )}

                {selectedFeedback.qrCodeSource && (
                  <div className="detail-item">
                    <label>QR Kod Kaynağı:</label>
                    <span>{selectedFeedback.qrCodeSource}</span>
                  </div>
                )}
              </div>

              <div className="detail-section">
                <label>Konu:</label>
                <p className="detail-text">{selectedFeedback.subject}</p>
              </div>

              <div className="detail-section">
                <label>Açıklama:</label>
                <p className="detail-text">{selectedFeedback.description}</p>
              </div>

              {selectedFeedback.adminResponse && (
                <div className="detail-section">
                  <label>Yönetici Yanıtı:</label>
                  <p className="detail-text admin-response">{selectedFeedback.adminResponse}</p>
                </div>
              )}

              {selectedFeedback.attachments.length > 0 && (
                <div className="detail-section">
                  <label>Ekler ({selectedFeedback.attachments.length}):</label>
                  <div className="attachments-list">
                    {selectedFeedback.attachments.map((attachment) => (
                      <div key={attachment.id} className="attachment-item">
                        <div className="attachment-info">
                          <span className="attachment-name">📎 {attachment.fileName}</span>
                          <span className="attachment-size">
                            ({Math.round(attachment.fileSize / 1024)} KB)
                          </span>
                          <span className="attachment-date">
                            {new Date(attachment.uploadedDate).toLocaleDateString('tr-TR')}
                          </span>
                        </div>
                        <div className="attachment-actions">
                          {isImage(attachment.contentType || '') && (
                            <a
                              href={`http://172.173.131.89:86/${attachment.filePath}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="view-button"
                            >
                              👁️ Görüntüle
                            </a>
                          )}
                          <button
                            onClick={() => downloadAttachment(attachment)}
                            className="download-button"
                          >
                            ⬇️ İndir
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackListPage;